import { type FC } from "react";

import type { AcquirerToTerminalEntity } from "src/app/features/Terminal/types/AcquirerToTerminalEntity";
import type { ExtendedColumnsType } from "../../../index";
import { PropertyTable } from "../../../Table/components/PropertyTable";
import { type AntCustomFormField, type CommonFieldAttributes } from "../../types/types";
import { TerminalHostIdGenerator } from "../TerminalIdGenerator/TerminalHostIdGenerator";
import { TextInput } from "../Text/TextInput";

export interface TerminalHostTerminalTableProps extends AntCustomFormField<AcquirerToTerminalEntity[]>, CommonFieldAttributes {}

export const TerminalHostTerminalTable: FC<TerminalHostTerminalTableProps> = ({ value = [], onChange }) => {
    // Keyed by acquirerId, not id: a row with no saved ACQUIRER_TO_TERMINAL record yet has id === null
    const onInput = (acquirerId: string, key: keyof AcquirerToTerminalEntity, newValue: string | boolean) => {
        const newData = value.map((row) => {
            if (row.acquirerId !== acquirerId) return row;
            return { ...row, [key]: newValue };
        });
        onDataChanged(newData);
    };

    const getColumnsConfig = (t: Function): ExtendedColumnsType<AcquirerToTerminalEntity>[] => {
        return [
            {
                title: t("terminals.hostids"),
                key: "id",
                dataIndex: "id",
            },
            {
                title: t("terminals.hostids.name"),
                key: "name",
                dataIndex: "name",
            },
            {
                title: t("terminals.hostids.tid"),
                key: "hostId",
                dataIndex: "hostId",
                render: (_, record) =>
                    record.readonly ? (
                        <TextInput
                            name={`hostId-${record.acquirerId}`}
                            label=""
                            value={record.hostId}
                            readOnly={record.readonly}
                            onChange={(newValue) => onInput(record.acquirerId!, "hostId", newValue)}
                        />
                    ) : (
                        <TerminalHostIdGenerator
                            name={`hostId-${record.acquirerId}`}
                            label=""
                            value={record.hostId}
                            readOnly={false}
                            inputIsDisabled={false}
                            terminalId={record.terminalId}
                            acquirerId={record.acquirerId}
                            onChange={(newValue) => onInput(record.acquirerId!, "hostId", newValue)}
                        />
                    ),
            },
            {
                title: t("terminals.hostids.acquirerId"),
                key: "acquirerId",
                dataIndex: "acquirerId",
                hidden: true,
            },
            {
                title: t("terminals.hostids.terminalId"),
                key: "terminalId",
                dataIndex: "terminalId",
                hidden: true,
            },
            {
                title: t("terminals.hostids.readOnly"),
                key: "readOnly",
                dataIndex: "readOnly",
                hidden: true,
            },
        ];
    };

    const onDataChanged = (data: AcquirerToTerminalEntity[]) => {
        if (onChange) {
            onChange(data);
        }
    };

    return (
        <PropertyTable<AcquirerToTerminalEntity>
            getColumnsConfig={getColumnsConfig}
            name="terminalHostTerminalId_table"
            propertyAsRowKey="acquirerId"
            tableData={value || []}
            readOnly={false}
            rowsAreSelectable={false}
            readOnlyVisibilityEnabled
            onDataChanged={onDataChanged}
        />
    );
};
